import { useStore } from 'vuex'
import { isFunction } from '@/utils/tools'
import { getEntryInfo } from '@/utils'

import { GetImagesInfo, getCrmAreaInfo } from '@/apis/public'
import { ref, computed, ComputedRef, Ref } from 'vue'
import {
  CommonCacheGetUploadInfo,
  GetCrmAreaInfoQuery,
  GetCrmAreaInfoResultItem,
  AuthLocationInfoType,
} from '@/types/public'
import type { CascaderOption } from 'vant'
import { ImageGuidUrlMapStorage } from '@/storage/common'
import { USER_NEARBY_WAREHOUSE_AUTH } from '@/config/constant'
import { showToastSuccess, showToastFail, showConfirmModal } from '@/utils/tools'
import ClipboardJS from 'clipboard'
import wxHost from '@/utils/wx'
import appHost, { isAndrod, getAppName, isApp } from '@/utils/app'
import { useUserPermissionVerify } from '@/compositions/useUserAuthPermission'

export function useUsermobileInfo(): {
  formatUserMobile: string
  userMobile: string
  isMiniprogram: boolean
  userMobileComputed: ComputedRef<string>
  formatUserMobileComputed: ComputedRef<string>
} {
  const vueStore = useStore()
  const userMobile = vueStore.state.userInfo ? vueStore.state.userInfo.mobile : ''
  const isMiniprogram = vueStore.getters.isMiniProgram
  const formatUserMobile = userMobile.slice(0, 3) + '****' + userMobile.slice(-4)

  const userMobileComputed = computed(() => {
    return vueStore.state.userInfo ? vueStore.state.userInfo.mobile : ''
  })
  const formatUserMobileComputed = computed(() => {
    const userMobile = userMobileComputed.value
    return userMobile.slice(0, 3) + '****' + userMobile.slice(-2)
  })
  return {
    userMobile,
    formatUserMobile,
    isMiniprogram,
    userMobileComputed,
    formatUserMobileComputed,
  }
}

export function usePhoneDomAdaption(): {
  phoneDomEmAdaption: ($selector: HTMLElement, maxWidth?: number) => void
} {
  function phoneDomEmAdaption($selector: HTMLElement, maxWidth?: number) {
    setPhoneEmFontSize($selector, Math.min(window.innerWidth, 750), maxWidth)
    window.addEventListener('resize', () => {
      const width = window.innerWidth
      setPhoneEmFontSize($selector, Math.min(width, 750), maxWidth)
    })
  }
  function setPhoneEmFontSize($selector: HTMLElement, currentWidth: number, maxWidth = 750) {
    $selector.style.fontSize =
      currentWidth >= Math.min(maxWidth) ? '100px' : currentWidth / 7.5 + 'px'
  }
  return {
    phoneDomEmAdaption,
  }
}

type GetImageFullUrlQueryOptions = {
  width: number
  height: number
}
export function useImageGuidTransfer(): {
  getImageFullUrl: (
    guids: string[],
    options?: GetImageFullUrlQueryOptions
  ) => Promise<CommonCacheGetUploadInfo>
} {
  async function getImageFullUrl(guids: string[], options?: GetImageFullUrlQueryOptions) {
    const width = options?.width || 150
    const height = options?.height || 150
    const cacheData: CommonCacheGetUploadInfo = ImageGuidUrlMapStorage.get() || {}
    const unCacheGuids: string[] = []
    const result: CommonCacheGetUploadInfo = {}
    guids.forEach((guid) => {
      const url = cacheData[guid]?.url || ''
      result[guid] = {
        guid,
        url,
      }
      if (!url) {
        unCacheGuids.push(guid)
      }
    })
    const data = unCacheGuids.length
      ? await GetImagesInfo({
          res_uids: unCacheGuids,
          width,
          height,
        })
      : []
    data.forEach((item) => {
      cacheData[item.uid] = {
        guid: item.uid,
        url: item.url,
      }
      result[item.uid].url = item.url
      ImageGuidUrlMapStorage.set(cacheData)
    })
    return result
  }
  return {
    getImageFullUrl,
  }
}

export function useClipboard(options: {
  text: string | (() => string)
  useDefaultSuccess?: boolean
  useDefaultFail?: boolean
  successMessage?: string
  failMessage?: string
  successHandle?: () => void
  failHandle?: () => void
}): {
  initClipboard: (id: string) => void
} {
  const text = options.text
  const {
    successMessage = '复制成功',
    failMessage = '复制失败',
    useDefaultSuccess = true,
    useDefaultFail = true,
    successHandle = () => undefined,
    failHandle = () => undefined,
  } = options
  function initClipboard(id: string) {
    const clipbordItem = new ClipboardJS(id, {
      text() {
        return typeof text === 'string' ? text : text()
      },
    })
    clipbordItem.on('success', function (e) {
      useDefaultSuccess && showToastSuccess(successMessage)
      successHandle()
      e.clearSelection()
    })

    clipbordItem.on('error', function (e) {
      useDefaultFail && showToastFail(failMessage)
      failHandle()
    })
  }
  return {
    initClipboard,
  }
}

// 获取当前经纬度
export function useLocactionInfo(): {
  getUserLocationLngLat: () => Promise<number[]>
  requestingLocationLngLat: Ref<boolean>
  getUserLocationLngLatWithAuth: (key: string) => Promise<{
    lat?: number | string
    lng?: number | string
    unAuth?: boolean
  }>
  initLocationInfo: (key: string) => Promise<{
    lat?: number | string
    lng?: number | string
    unAuth?: boolean
  }>
  locationInfo: Ref<AuthLocationInfoType>
  initNearbyWarehouseHandle: () => void
} {
  const vueStore = useStore()
  const locationInfo: Ref<AuthLocationInfoType> = ref({})
  const requestingLocationLngLat = ref(false)
  const isMiniprogram = vueStore.getters.isMiniProgram

  function getUnLocationPermissionMessage() {
    if (isMiniprogram) {
      return '请在系统“设置”中开启微信定位权限和手机定位服务。'
    } else {
      return `请在系统“设置”中开启${getAppName()} App 定位权限和手机定位服务。`
    }
  }
  function getUserLocationLngLat(): Promise<number[]> {
    // 获取经纬度
    requestingLocationLngLat.value = true
    function hideLoading() {
      requestingLocationLngLat.value = false
    }
    const promise: Promise<number[]> = new Promise((resolve, reject) => {
      // 由于没有定位权限就会导致一直不响应，因此设置3s的最大时间，超过判断为没有权限
      const limitTime = 3000
      let isTimeout = false // 是否已超时
      let timeId = 0
      if (isMiniprogram) {
        wxHost
          .startlocationPromise()
          .then((data) => {
            console.log('data', data)
            hideLoading()
            resolve(data)
          })
          .catch((e) => {
            // 以没有权限获取经纬度处理
            hideLoading()
            reject({
              message: e?.errMsg || getUnLocationPermissionMessage(),
            })
          })
      } else {
        appHost.startLocation(true, (lng: number, lat: number) => {
          const value = [lng, lat]
          clearTimeout(timeId) // 成功响应时清除定时器
          if (!isTimeout) {
            if (isAndrod) {
              // app安卓农服旧版本特有的bug, 可尝试重启app解决
              if (String(lng) === '5e-324' || String(lat) === '5e-324') {
                hideLoading()
                reject({
                  message: '获取经纬度失败，请重启 App 后再试。',
                })
              }
            }
            if (lng === 0 || lat === 0 || String(lng) === '0.0' || String(lat) === '0.0') {
              hideLoading()
              reject({
                message: '获取经纬度失败。', // 未知情况下获取经纬度为0
              })
            }
            hideLoading()
            resolve(value)
          }
        })
        timeId = setTimeout(() => {
          isTimeout = true
          hideLoading()
          reject({
            message: getUnLocationPermissionMessage(),
          })
        }, limitTime)
      }
    })
    promise.finally &&
      isFunction(promise.finally) &&
      promise.finally(() => {
        requestingLocationLngLat.value = false
      })
    return promise
  }
  // 在获取经纬度之前再处理多一次
  async function getUserLocationLngLatWithAuth(key: string): Promise<{
    lat?: number | string
    lng?: number | string
    unAuth?: boolean
  }> {
    if (!isApp) {
      if (!key) {
        const message = '获取经纬度失败，未提供验证的key'
        console.warn(message)
        return Promise.reject({
          message: message,
        })
      }
      const { checkAuthorizationHandle, hasAuthorized } = useUserPermissionVerify(key)
      if (!hasAuthorized.value) {
        const userAgree = await checkAuthorizationHandle()
        if (!userAgree) {
          console.warn('用户不同意获取经纬度')
          return {
            unAuth: true,
          }
        }
      }
    }

    try {
      const data = await getUserLocationLngLat()
      return {
        lng: data[0],
        lat: data[1],
      }
    } catch (error) {
      return Promise.reject(error)
    }
  }
  // 初始化经纬度信息
  async function initLocationInfo(key: string) {
    try {
      const data = await getUserLocationLngLatWithAuth(key)
      locationInfo.value = data
      return data
    } catch (error) {
      return Promise.reject(error)
    }
  }
  // 获取附近网点级的经纬度权限的处理方案
  async function initNearbyWarehouseHandle() {
    let errorMessage = ''
    try {
      const data = await initLocationInfo(USER_NEARBY_WAREHOUSE_AUTH)
      if (data.unAuth) {
        errorMessage = '获取不到经纬度信息，无法获取附近的网点信息'
      }
    } catch (error) {
      errorMessage = (error as { message: string })?.message
    }
    if (errorMessage) {
      try {
        await showConfirmModal({
          title: '提示',
          confirmButtonText: '重新授权',
          cancelButtonText: '关闭',
          showCancelButton: true,
          message: errorMessage || '当前无法根据您的位置信息获取附近的网点展示，请知悉',
          className: 'nongfu-dialog-confirm-class nongfu-dialog-message-center',
        })
        // 重新授权时重新获取
        initNearbyWarehouseHandle()
      } catch (error) {
        console.log('error', error)
      }
    }
  }
  return {
    getUserLocationLngLat,
    requestingLocationLngLat,
    getUserLocationLngLatWithAuth,
    initLocationInfo,
    locationInfo,
    initNearbyWarehouseHandle,
  }
}

// 获取当设备信息
export function usePlatformInfo(): {
  judgeIsSrc4Device: () => Promise<boolean>
  isLandscape: ComputedRef<boolean>
  isSrc4Device: ComputedRef<boolean>
  isTableDevice: ComputedRef<boolean>
} {
  const vueStore = useStore()
  const isLandscape = computed(() => vueStore.state.isLandscape)
  const isSrc4Device = computed(() => vueStore.state.isSrc4Device)
  const isTableDevice = computed(() => vueStore.state.isTableDevice)
  async function judgeIsSrc4Device() {
    const data = await appHost.getAppBaseInfo()
    const platform = data.platform || ''
    return platform.toLocaleUpperCase().indexOf('SRC') !== -1
  }

  return {
    judgeIsSrc4Device,
    isLandscape,
    isSrc4Device,
    isTableDevice,
  }
}
const cacheIdLevelMap: Record<number | string, number> = {}
const cacheAreaParentChildrenDataMap: Record<string | number, GetCrmAreaInfoResultItem[]> = {}

// 获取地址信息
export function useCrmAreaInfo(option?: { max: number }): {
  cascaderOptionValueMap: Record<string, CascaderOption>
  getAreaInfo: (params?: GetCrmAreaInfoQuery) => Promise<GetCrmAreaInfoResultItem[]>
  getAreaCascaderOptions: (params?: GetCrmAreaInfoQuery) => Promise<CascaderOption[]>
} {
  const max = option?.max || 100
  const cascaderOptionValueMap: Record<string, CascaderOption> = {}

  async function getAreaInfo(params?: GetCrmAreaInfoQuery) {
    try {
      const id = params?.p_area_id || 0
      if (cacheAreaParentChildrenDataMap[id]) {
        return cacheAreaParentChildrenDataMap[id]
      }
      const level = cacheIdLevelMap[id] ? cacheIdLevelMap[id] + 1 : 1
      const result = await getCrmAreaInfo({
        p_area_id: id,
      })
      // const level =
      result.map((item) => {
        cacheIdLevelMap[item.area_id] = level
        return {
          ...item,
          level,
        }
      })
      cacheAreaParentChildrenDataMap[id] = result.map((item) => ({ ...item, level }))
      return result.map((item) => ({ ...item, level }))
    } catch (error) {
      console.log('error', error)
      return Promise.reject(error)
    }
  }
  async function getAreaCascaderOptions(params?: GetCrmAreaInfoQuery): Promise<CascaderOption[]> {
    try {
      const data = await getAreaInfo(params)
      return data.map((item) => {
        const result: any = {
          text: item.area_name,
          value: item.area_id + '',
          parentId: params?.p_area_id,
        }
        if (item.has_son && item.level < max) {
          result.children = []
        }
        cascaderOptionValueMap[result.value] = { ...result }
        return result
      })
    } catch (error) {
      return Promise.reject(error)
    }
  }
  return {
    getAreaInfo,
    getAreaCascaderOptions,
    cascaderOptionValueMap,
  }
}
// 判断是否使用原生标题栏
export function useNavInfo() {
  const vueStore = useStore()
  const appStatusBarHeight = computed<number>(() =>
    vueStore.state.isLandscape ? 0 : vueStore.state.appStatusBarHeight
  )
  const navHeaderHeight = computed(() => {
    return vueStore.getters.isMiniProgram ? 0 : 44
  })
  function judgeIsUseAppDefaultNav() {
    const entryInfo = getEntryInfo()
    return vueStore.getters.isMiniProgram || entryInfo?.from === 'banner'
  }

  function getWebviewNavHeightValue() {
    return appStatusBarHeight.value + navHeaderHeight.value
  }
  return {
    judgeIsUseAppDefaultNav,
    getWebviewNavHeightValue,
  }
}
// 获取overlay通用信息的hooks
export function useOverlayCommonInfo() {
  const { judgeIsUseAppDefaultNav, getWebviewNavHeightValue } = useNavInfo()

  const overlayHeightValue = computed<string>(() => {
    const weiviewNavHeight = getWebviewNavHeightValue()
    if (judgeIsUseAppDefaultNav()) {
      return '100vh'
    }
    return `calc(100vh - ${weiviewNavHeight}px)`
  })

  return {
    overlayHeightValue,
  }
}
